import type { FormEvent } from 'react';
import { apiFieldErrors } from './api-errors';

export function fieldError(field: {
  state: {
    meta: { isTouched: boolean; errors: readonly unknown[]; errorMap?: { onSubmit?: unknown } };
  };
}): string | undefined {
  if (!field.state.meta.isTouched && !field.state.meta.errorMap?.onSubmit) return undefined;
  const messages = field.state.meta.errors.flatMap((error) => {
    if (typeof error === 'string') return [error];
    if (
      error &&
      typeof error === 'object' &&
      'message' in error &&
      typeof error.message === 'string'
    )
      return [error.message];
    return [];
  });
  return [...new Set(messages)].join(' ') || undefined;
}

export function getFormFieldErrors(error: unknown): Record<string, string> {
  if (!error || typeof error !== 'object') return {};
  const fields =
    'fieldErrors' in error && error.fieldErrors && typeof error.fieldErrors === 'object'
      ? error.fieldErrors
      : 'details' in error
        ? apiFieldErrors(error.details, {}, true)
        : {};
  return Object.fromEntries(
    Object.entries(fields)
      .filter((entry): entry is [string, string] => typeof entry[1] === 'string')
      .map(([path, message]) => [path.replace(/\.(\d+)(?=\.|$)/g, '[$1]'), message]),
  );
}

export function focusFirstInvalid(element: HTMLFormElement | null): void {
  requestAnimationFrame(() => {
    if (!element?.isConnected) return;
    const selector = '[aria-invalid="true"]:not([disabled])';
    const associated = Array.from(element.elements).find(
      (control): control is HTMLElement =>
        control instanceof HTMLElement && control.matches(selector),
    );
    const control = associated ?? element.querySelector<HTMLElement>(selector);
    control?.focus();
  });
}

export async function submitForm(
  event: FormEvent<HTMLFormElement>,
  form: { handleSubmit(): Promise<unknown> },
): Promise<void> {
  event.preventDefault();
  event.stopPropagation();
  const element = event.currentTarget;
  await form.handleSubmit();
  focusFirstInvalid(element);
}
